import { useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";
import { Menu } from "lucide-react";
import { tokenStorage } from "../../../utils/tokenStorage";
import { Brand, Button } from "../../ui";
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "../Sheet";
import { PublicNavbarActions } from "./PublicNavbarActions";

const NAV_LINKS = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About Fundy" },
  { to: "/how-it-works", label: "How it works" },
  { to: "/contact", label: "Contact" },
] as const;

export const PublicNavbar = () => {
  const isAuthenticated = Boolean(tokenStorage.getAccessToken());
  const brandTo = isAuthenticated ? "/app" : "/";
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 80);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // iOS Safari ignores overflow:hidden on body, so Radix's scroll-lock doesn't
  // prevent the page from scrolling behind an open sheet. Close it on scroll instead.
  useEffect(() => {
    if (!mobileMenuOpen) return;
    const onScroll = () => setMobileMenuOpen(false);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [mobileMenuOpen]);

  return (
    <header
      className={`fixed left-0 right-0 z-[--z-navbar] flex h-[--navbar-height] items-center border-b px-6 transition-all duration-300 ease-out sm:px-8 md:px-10 lg:px-12 ${
        scrolled
          ? "top-0 border-black/10 bg-white/95 shadow-sm backdrop-blur-md"
          : "top-4 border-transparent bg-black/10 backdrop-blur-sm"
      }`}
    >
      <div className="flex w-full items-center justify-between">
        <Brand to={brandTo} className={scrolled ? "text-gray-900" : "text-white"} />

        <div className="flex items-center gap-6">
          {/* Desktop nav links */}
          <nav className="hidden items-center gap-6 lg:flex">
            {NAV_LINKS.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={`rounded-sm text-[16px] font-medium transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                  scrolled ? "text-gray-600 hover:text-gray-900" : "text-white/90 hover:text-white"
                }`}
                activeProps={{
                  className: "!text-primary font-semibold",
                }}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-3">
          {/* Desktop pill group */}
          <PublicNavbarActions isAuthenticated={isAuthenticated} variant="desktop" />

          {/* Mobile menu */}
          <div className="lg:hidden">
            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild>
                <Button
                  variant="outline"
                  size="icon"
                  className="shrink-0 border-background/30 bg-card/90 text-foreground backdrop-blur hover:bg-card"
                >
                  <Menu className="h-5 w-5" />
                  <span className="sr-only">Toggle navigation menu</span>
                </Button>
              </SheetTrigger>
              <SheetContent side="left">
                <SheetTitle className="sr-only">Navigation menu</SheetTitle>
                <nav className="grid gap-4 text-base font-medium">
                  <Brand to={brandTo} className="justify-start" />
                  {NAV_LINKS.map((link) => (
                    <Link
                      key={link.to}
                      to={link.to}
                      className="rounded-sm text-[16px] font-medium text-foreground transition-colors duration-150 hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                      activeProps={{ className: "!text-primary font-semibold" }}
                    >
                      {link.label}
                    </Link>
                  ))}
                  <div className="mt-2">
                    <PublicNavbarActions isAuthenticated={isAuthenticated} variant="mobile" />
                  </div>
                </nav>
              </SheetContent>
            </Sheet>
          </div>
        </div>
        </div>
      </div>
    </header>
  );
};
